import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Star, Clock, Video, CalendarCheck, Loader2, Check, X, LogIn, MessageSquareQuote,
} from "lucide-react";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal, Chapter } from "../components/Reveal";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";

const inputClass =
  "h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

const fmtDay = (iso, f) => {
  try {
    return format(new Date(`${iso}T00:00:00`), f);
  } catch {
    return iso;
  }
};

function BookingSheet({ tutor, user, onClose, onBooked }) {
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => addDays(new Date(), i)), []);
  const [date, setDate] = useState(format(addDays(new Date(), 1), "yyyy-MM-dd"));
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [topic, setTopic] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    setSlot(null);
    setSlots(null);
    api
      .get(`/tutors/${tutor.id}/availability`, { params: { date } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [tutor.id, date]);

  const book = async () => {
    if (!slot) return toast.error("Please pick a time slot");
    if (name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email");
    setSubmitting(true);
    try {
      const { data } = await api.post("/tutoring/book", {
        tutor_id: tutor.id, date, time: slot, topic: topic || null,
        name, email, notes: null,
      });
      setDone(data);
      onBooked();
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 409) {
        setSlot(null);
        api.get(`/tutors/${tutor.id}/availability`, { params: { date } }).then((r) => setSlots(r.data.slots));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SheetContent side="right" className="w-full overflow-y-auto bg-bone p-0 sm:max-w-lg" data-testid="booking-sheet">
      <div className="bg-forest-deep px-7 py-6">
        <SheetTitle className="font-display text-lg font-bold text-bone">Book a session</SheetTitle>
        <p className="mt-1 text-xs text-emerald-100/60">
          {tutor.name} · {tutor.subject} · ${tutor.rate}/session
        </p>
      </div>

      {done ? (
        <div className="px-7 py-12 text-center" data-testid="booking-success">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold shadow-[0_0_35px_rgba(212,175,55,0.45)]">
            <CalendarCheck className="h-7 w-7 text-forest-deep" />
          </span>
          <h3 className="mt-6 font-display text-2xl font-bold text-forest-deep">Session booked</h3>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
            <span className="font-semibold text-forest">{done.tutor_name}</span> ·{" "}
            {fmtDay(done.date, "EEEE, MMMM d")} at {done.time} GMT.
            A confirmation email is on its way to {done.email} — your meeting link will be sent before the session.
          </p>
          <button onClick={onClose} data-testid="booking-done-btn"
            className="mt-7 rounded-full bg-forest px-8 py-3 font-display text-sm font-bold text-bone">
            Done
          </button>
        </div>
      ) : (
        <div className="space-y-7 px-7 py-7">
          <div>
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">1 · Pick a day</p>
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="booking-date-strip">
              {days.map((d) => {
                const iso = format(d, "yyyy-MM-dd");
                const active = date === iso;
                return (
                  <button key={iso} data-testid={`booking-day-${iso}`} onClick={() => setDate(iso)}
                    className={`flex w-16 shrink-0 flex-col items-center rounded-2xl border px-2 py-3 transition-colors duration-200 ${
                      active ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/70 hover:border-gold-dark/50"
                    }`}>
                    <span className={`font-mono text-[9px] uppercase tracking-widest ${active ? "text-gold" : "text-muted-foreground"}`}>
                      {format(d, "EEE")}
                    </span>
                    <span className="mt-1 font-display text-lg font-bold">{format(d, "d")}</span>
                    <span className={`text-[9px] ${active ? "text-emerald-100/70" : "text-muted-foreground"}`}>{format(d, "MMM")}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">
              2 · Pick a time <span className="text-muted-foreground normal-case tracking-normal">(GMT)</span>
            </p>
            {slots === null ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-forest" /></div>
            ) : (
              <div className="grid grid-cols-4 gap-2" data-testid="booking-slots">
                {slots.map((s) => (
                  <button key={s.time} data-testid={`booking-slot-${s.time.replace(":", "")}`}
                    disabled={!s.available} onClick={() => setSlot(s.time)}
                    className={`rounded-xl border py-2.5 font-mono text-xs font-semibold transition-colors duration-200 ${
                      !s.available
                        ? "cursor-not-allowed border-border/50 bg-secondary/50 text-muted-foreground/40 line-through"
                        : slot === s.time
                          ? "border-forest bg-forest text-bone"
                          : "border-border bg-white text-foreground/70 hover:border-gold-dark/50"
                    }`}>
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">3 · Your details</p>
            <div className="space-y-3">
              <input data-testid="booking-topic-input" className={inputClass} value={topic}
                onChange={(e) => setTopic(e.target.value)} placeholder="What do you want to work on? (optional)" />
              <input data-testid="booking-name-input" className={inputClass} value={name}
                onChange={(e) => setName(e.target.value)} placeholder="Full name" autoComplete="name" />
              <input data-testid="booking-email-input" type="email" className={inputClass} value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="Email address" autoComplete="email" />
            </div>
          </div>

          <button onClick={book} data-testid="booking-confirm-btn" disabled={submitting || !slot}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.4)] disabled:opacity-50">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirm booking{slot ? ` — ${fmtDay(date, "MMM d")} at ${slot}` : ""}
          </button>
        </div>
      )}
    </SheetContent>
  );
}

function RateBlock({ session, onRated }) {
  const [stars, setStars] = useState(session.rating || 0);
  const [feedback, setFeedback] = useState(session.feedback || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!stars) return toast.error("Pick a star rating first");
    setSaving(true);
    try {
      await api.post(`/tutoring/sessions/${session.id}/rate`, { rating: stars, feedback: feedback || null });
      toast.success("Thanks for the feedback");
      onRated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (session.rating) {
    return (
      <div className="mt-3 flex items-center gap-1" data-testid={`rated-${session.id}`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`h-4 w-4 ${n <= session.rating ? "fill-gold text-gold" : "text-border"}`} />
        ))}
        <span className="ml-2 text-xs text-muted-foreground">You rated this session</span>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-gold/25 bg-gold/5 p-4" data-testid={`rate-block-${session.id}`}>
      <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-gold-dark">
        <MessageSquareQuote className="h-3.5 w-3.5" /> How was it?
      </p>
      <div className="mt-2.5 flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`rate-star-${session.id}-${n}`} onClick={() => setStars(n)}
            className="transition-transform duration-150 hover:scale-110 active:scale-95">
            <Star className={`h-6 w-6 ${n <= stars ? "fill-gold text-gold" : "text-forest/25"}`} />
          </button>
        ))}
      </div>
      <textarea data-testid={`rate-feedback-${session.id}`} rows={2} value={feedback}
        onChange={(e) => setFeedback(e.target.value)} placeholder="A quick note for your tutor (optional)"
        className="mt-3 w-full rounded-xl border border-input bg-white p-3 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/25" />
      <button onClick={submit} data-testid={`rate-submit-${session.id}`} disabled={saving}
        className="mt-3 flex items-center gap-2 rounded-full bg-forest px-5 py-2 text-xs font-bold text-bone disabled:opacity-60">
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit rating
      </button>
    </div>
  );
}

function MySessions({ user, refreshKey, onChanged }) {
  const [sessions, setSessions] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const load = useCallback(() => {
    if (!user) return;
    api.get("/tutoring/my-sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, [user]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!user) {
    return (
      <div className="rounded-[2rem] border border-dashed border-forest/25 bg-white p-10 text-center" data-testid="sessions-login-prompt">
        <LogIn className="mx-auto h-8 w-8 text-forest/30" />
        <p className="mt-4 font-display text-lg font-bold text-forest-deep">Your sessions live here</p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
          Log in (or create a free account) to see upcoming sessions, cancel, and rate your tutors.
        </p>
        <Link to="/login" data-testid="sessions-login-btn"
          className="mt-5 inline-block rounded-full bg-forest px-7 py-3 font-display text-sm font-bold text-bone">
          Log in
        </Link>
      </div>
    );
  }

  if (sessions === null) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  }

  if (sessions.length === 0) {
    return (
      <p className="rounded-[2rem] border border-dashed border-forest/25 bg-white py-12 text-center text-sm text-muted-foreground" data-testid="sessions-empty">
        No sessions yet — book your first one above.
      </p>
    );
  }

  const cancel = async (s) => {
    if (confirmCancel !== s.id) {
      setConfirmCancel(s.id);
      setTimeout(() => setConfirmCancel((c) => (c === s.id ? null : c)), 3000);
      return;
    }
    try {
      await api.post(`/tutoring/sessions/${s.id}/cancel`);
      toast.success("Session cancelled");
      setConfirmCancel(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="sessions-list">
      {sessions.map((s) => (
        <div key={s.id} data-testid={`session-card-${s.id}`}
          className="flex flex-col gap-4 rounded-3xl border border-border bg-white p-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-forest font-display text-lg font-bold text-bone">
              {s.tutor_name[0]}
            </span>
            <div>
              <p className="font-display text-base font-bold text-forest-deep">{s.tutor_name}</p>
              <p className="text-xs text-muted-foreground">{s.tutor_subject}</p>
              <p className="mt-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
                <CalendarCheck className="h-4 w-4 text-gold-dark" />
                {fmtDay(s.date, "EEEE, MMMM d, yyyy")} · {s.time} GMT
              </p>
              {s.topic && <p className="mt-1 text-xs italic text-muted-foreground">“{s.topic}”</p>}
              {!s.past && s.status === "upcoming" && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Meeting link will be emailed to you before the session.
                </p>
              )}
              {s.past && s.status !== "cancelled" && <RateBlock session={s} onRated={onChanged} />}
            </div>
          </div>
          <div className="flex shrink-0 flex-row items-center gap-3 sm:flex-col sm:items-end">
            <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
              s.status === "cancelled"
                ? "border-stone-200 bg-stone-100 text-stone-500"
                : s.past
                  ? "border-forest/25 bg-forest/10 text-forest"
                  : "border-gold/40 bg-gold/15 text-gold-dark"
            }`}>
              {s.status === "cancelled" ? "Cancelled" : s.past ? "Completed" : "Upcoming"}
            </span>
            {!s.past && s.status === "upcoming" && (
              <button onClick={() => cancel(s)} data-testid={`session-cancel-${s.id}`}
                className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  confirmCancel === s.id
                    ? "border-destructive bg-destructive text-white"
                    : "border-border text-destructive/70 hover:border-destructive/50"
                }`}>
                <X className="h-3.5 w-3.5" /> {confirmCancel === s.id ? "Confirm cancel?" : "Cancel"}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TutoringPage() {
  const { user } = useAuth();
  const [tutors, setTutors] = useState(null);
  const [bookingTutor, setBookingTutor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    api.get("/tutors").then((r) => setTutors(r.data)).catch(() => setTutors([]));
  }, []);

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="tutoring-page">
      <div className="relative overflow-hidden bg-forest-deep">
        <div className="bg-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/50" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-bone">Home</Link>
              <span>/</span><span>Services</span><span>/</span>
              <span className="text-gold">Online Tutoring</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl">
              One-on-one tutoring that{" "}
              <em className="font-editorial font-medium italic text-gold">moves the needle.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
              Book a vetted expert, pick a time that suits you, and get a secure session link by email.
              Rate every session — our tutors are accountable to your progress.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter index="01" label="Choose your tutor" />
        </Reveal>
        <div className="mt-8 grid gap-5 md:grid-cols-2" data-testid="tutors-grid">
          {tutors === null ? (
            <div className="col-span-2 flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
          ) : (
            tutors.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.07}>
                <article data-testid={`tutor-card-${i}`}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_20px_50px_rgba(10,41,30,0.1)]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-forest font-display text-xl font-extrabold text-bone">
                        {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </span>
                      <div>
                        <h3 className="font-display text-lg font-bold text-forest-deep">{t.name}</h3>
                        <p className="text-xs font-medium text-gold-dark">{t.subject}</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 rounded-full bg-gold/10 px-2.5 py-1 text-xs font-bold text-gold-dark">
                      <Star className="h-3.5 w-3.5 fill-gold text-gold" /> {t.rating}
                    </span>
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{t.bio}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {t.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-secondary px-3 py-1 text-[11px] font-semibold text-forest/80">{tag}</span>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-5">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-display text-xl font-extrabold text-forest-deep">${t.rate}</span> /session ·{" "}
                      {t.sessions_held}+ held
                    </p>
                    <button onClick={() => setBookingTutor(t)} data-testid={`book-tutor-${i}`}
                      className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(19,78,58,0.35)]">
                      <Video className="h-4 w-4" /> Book
                    </button>
                  </div>
                </article>
              </Reveal>
            ))
          )}
        </div>

        <div className="mt-16">
          <Reveal>
            <Chapter index="02" label="My sessions" />
          </Reveal>
          <div className="mt-8">
            <MySessions user={user} refreshKey={refreshKey} onChanged={refresh} />
          </div>
        </div>
      </div>

      <Sheet open={!!bookingTutor} onOpenChange={(o) => !o && setBookingTutor(null)}>
        {bookingTutor && (
          <BookingSheet tutor={bookingTutor} user={user || {}}
            onClose={() => setBookingTutor(null)} onBooked={refresh} />
        )}
      </Sheet>
    </div>
  );
}
